<script setup>
import { getCurrentInstance, onMounted, ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import SiteFormInputs from '@/components/sites/SiteFormInputs.vue';
import PricingTable from '@/components/sites/PricingTable.vue';

defineOptions({
    layout: (h, page) => {
        const isAdminContext = window.location.pathname.startsWith('/admin/');
        return isAdminContext
            ? h(AdminMainLayout, () => page)
            : h(MainLayout, () => page);
    },
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

// URL validation states
const urlValidation = ref({
    status: '', // 'error' | 'warning' | ''
    message: '',
    sites: [],
    checking: false
});

const props = defineProps({
    coins: Object,
    categories: Array,
    languages: Array,
    countries: Array,
    sellers: Array,
    teams: Array,
    types: Array,
    exchangeData: Object,
});

const form = useForm({
    url: '',
    category_id: null,
    da: '',
    dr: '',
    traffic: '',
    language_id: null,
    country_id: null,
    link_type: null,
    branded_content: false,

    gambling: false,
    menu: false,
    banner: false,

    last_posted: '',
    wpp_group: '',
    validation_date: null,
    mark_as_published: false,
    seller_id: null,
    team_id: null,

    obs: '',
    type_observation: '',

    owner_name: '',
    owner_email: '',
    owner_phone: '',
    bank: '',
    pix: '',
    phone: '',
    paypal: '',
    instagram: '',
    facebook: '',

    owner_role: '',
    promo: false,
    restrict: false,
    restrict_detail: '',
    archive_article: false,
    archive_due: '',
    links: null,
    embed: false,
    images: null,
    example_article: '',
    rules: '',
    google_news: false,
    global_account: '',

    types: props.types,
    auto_approve: true,
});

const submit = () => {
    // Block submission if there's an error (duplicate URL)
    if (urlValidation.value.status === 'error') {
        return;
    }

    const hasSelectedLinkType = form.link_type
    if (!hasSelectedLinkType) {
        if (proxy.$toast) {
            proxy.$toast.add({
                severity: 'error',
                summary: proxy.$t('Error'),
                detail: proxy.$t('Please select a Link type'),
                life: 3000
            });
        }
        return;
    }

    // Check if Default type has pricing filled
    // The Default type uses form.sale (not form.types[].sale)
    let saleValue = form.sale;

    // Handle different input formats (string with comma, string with dot, number)
    if (saleValue) {
        saleValue = saleValue.toString().replace(',', '.');
    }

    const parsedValue = saleValue ? parseFloat(saleValue) : 0;

    // Check if seller is selected
    if (!form.seller_id) {
        if (proxy.$toast) {
            proxy.$toast.add({
                severity: 'error',
                summary: proxy.$t('Error'),
                detail: proxy.$t('Please select a seller'),
                life: 3000
            });
        }
        return;
    }

    // Check if at least one type is available (marked as available)
    const availableTypes = form.types.filter(type => type.available);
    if (availableTypes.length === 0) {
        if (proxy.$toast) {
            proxy.$toast.add({
                severity: 'error',
                summary: proxy.$t('Error'),
                detail: proxy.$t('Please select at least one type and fill in the pricing'),
                life: 3000
            });
        }
        return;
    }

    // Validate that all available types have pricing filled (simplified check)
    let hasValidPricing = false;
    for (const type of availableTypes) {
        const cost = (type.cost || '').toString().replace(',', '.');
        const sale = (type.sale || '').toString().replace(',', '.');
        
        // For non-Linkarme sellers, only cost is required
        if (isLinkarmeSelected.value) {
            if (cost && sale && cost !== '0' && sale !== '0') {
                hasValidPricing = true;
                break;
            }
        } else {
            if (cost && cost !== '0') {
                hasValidPricing = true;
                break;
            }
        }
    }
    
    if (!hasValidPricing) {
        const requiredFields = isLinkarmeSelected.value ? 'cost and sale price' : 'cost price';
        if (proxy.$toast) {
            proxy.$toast.add({
                severity: 'error',
                summary: proxy.$t('Error'),
                detail: proxy.$t(`Please fill in ${requiredFields} for at least one selected type`),
                life: 3000
            });
        }
        return;
    }

    form.post(proxy.route ? proxy.route('sites.store') : '/admin/sites', {
        onStart: () => showLoading('Creating site...'),
        onFinish: () => hideLoading(),
        onError: (errors) => {
            console.error('Form submission errors:', errors);
            if (proxy.$toast) {
                proxy.$toast.add({
                    severity: 'error',
                    summary: proxy.$t('Error'),
                    detail: proxy.$t('Failed to create site.'),
                    life: 5000
                });
            }
        },
        onSuccess: () => {
            if (proxy.$toast) {
                proxy.$toast.add({
                    severity: 'success',
                    summary: proxy.$t('Success'),
                    detail: proxy.$t('Site created successfully'),
                    life: 3000
                });
            }
        }
    });
}

// Extract domain from URL
const extractDomain = (url) => {
    try {
        // Add protocol if missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch (e) {
        return url;
    }
}

// Check for duplicate or similar sites
const checkDuplicateSites = async () => {
    if (!form.url || form.url.trim() === '') {
        urlValidation.value = {
            status: '',
            message: '',
            sites: [],
            checking: false
        };
        return;
    }

    urlValidation.value.checking = true;

    try {
        const domain = extractDomain(form.url);
        const checkUrl = proxy.route ?
            proxy.route('sites.checkDuplicate', { url: form.url, domain: domain }) :
            `/admin/sites/check-duplicate?url=${encodeURIComponent(form.url)}&domain=${encodeURIComponent(domain)}`;

        // Make API request to check for duplicates/similar sites
        const response = await fetch(checkUrl, {
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        const data = await response.json();
        const { exact_match, similar_sites = [] } = data;

        if (exact_match) {
            urlValidation.value = {
                status: 'error',
                message: $t('Este domínio já está cadastrado'),
                sites: [exact_match, ...(similar_sites || [])],
                checking: false
            };
        } else if (similar_sites && similar_sites.length > 0) {
            urlValidation.value = {
                status: 'warning',
                message: $t('Domínios semelhantes encontrados. Verifique antes de continuar'),
                sites: similar_sites,
                checking: false
            };
        } else {
            urlValidation.value = {
                status: '',
                message: '',
                sites: [],
                checking: false
            };
        }
    } catch (error) {
        console.error('Error checking duplicate sites:', error);
        urlValidation.value = {
            status: '',
            message: '',
            sites: [],
            checking: false
        };
    }
}

const linkOptions = [
    { label: 'Selecione', value: null },
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' },
    { label: 'SPONSOR', value: 'SPONSOR' },
];

const linkLimitOptions = [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null }
];

const imageLimitOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
];

const currencyOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' }
];

// Format currency inputs with two decimal places
const formatCurrency = (value) => {
    if (!value || value === '') return '';

    // Remove all non-numeric characters except dots and commas
    let cleanValue = value.toString().replace(/[^\d.,]/g, '');

    cleanValue = cleanValue.replace(',', '.');

    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) return '';

    return numValue.toFixed(2).replace('.', ',');
}

const formatDate = (date) => {
    if(!date) return ''
    return date.toISOString().split('T')[0]
}

const isLinkarmeSelected = computed(() => {
    if (!form.seller_id || !props.sellers) return false;
    
    const selectedSellerObj = props.sellers.find(seller => seller.id === form.seller_id);
    return selectedSellerObj?.linkarme_seller === true || selectedSellerObj?.linkarme_seller === 1;
});

onMounted(() => {
    // Initialize form with proper structure and sort Default first
    form.types = form.types
        .map(type => ({
            ...type,
            isDefault: type.name === 'Default', // Mark Default type
            available: false, // All types start unchecked, including Default
            cost: '',
            cost_coin: 'BRL',
            sale: '',
            sale_coin: 'BRL',
            accepted_anchors: 0
        }))
        .sort((a, b) => {
            // Default type always comes first
            if (a.name === 'Default') return -1;
            if (b.name === 'Default') return 1;
            return 0;
        });
});
</script>

<template>
    <div class="grid">
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center gap-3 p-6 pb-0">
                    <i class="pi pi-plus text-2xl text-primary-500" />
                    <div>
                        <h2 class="text-xl font-bold">{{ $t('Add site') }}</h2>
                        <span class="text-sm text-surface-500">{{ $t('Create a new site entry') }}</span>
                    </div>
                </div>
            </template>

            <template #content>
                <form @submit.prevent="submit">
                    <!-- Site Form Inputs Component -->
                    <SiteFormInputs
                        :form="form"
                        :categories="categories"
                        :languages="languages"
                        :countries="countries"
                        :sellers="sellers"
                        :linkOptions="linkOptions"
                        :linkLimitOptions="linkLimitOptions"
                        :imageLimitOptions="imageLimitOptions"
                        :urlValidation="urlValidation"
                        :checkDuplicateSites="checkDuplicateSites"
                        :formatDate="formatDate"
                        :showUrlValidation="true"
                        :showSeller="true"
                    />

                    <Divider class="col-span-12 my-1" />

                    <!-- Pricing Table Component -->
                    <div class="col-span-12">
                        <PricingTable
                            :types="form.types"
                            :currencyOptions="currencyOptions"
                            v-model:defaultCost="form.cost"
                            v-model:defaultCostCoin="form.cost_coin"
                            v-model:defaultSale="form.sale"
                            v-model:defaultSaleCoin="form.sale_coin"
                            :formatCurrency="formatCurrency"
                            :isEditMode="false"
                            :canEdit="true"
                            :exchangeData="exchangeData"
                            :sellers="sellers"
                            v-model:selectedSeller="form.seller_id"
                            :isLinkarme="isLinkarmeSelected"
                        />
                    </div>

                    <Divider class="col-span-12 my-1" />

                    <!-- Auto Approve -->
                    <div class="col-span-12">
                        <div class="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                            <Checkbox v-model="form.auto_approve" inputId="auto_approve" :binary="true" />
                            <label for="auto_approve" class="ml-3">
                                <span class="font-semibold text-sm text-surface-900 dark:text-surface-100">{{ $t('Auto-approve this site') }}</span>
                                <span class="block text-xs text-surface-600 dark:text-surface-400 mt-0.5">
                                    {{ $t('If enabled, the site will be automatically approved and available for clients immediately after creation.') }}
                                </span>
                            </label>
                        </div>
                        <small v-if="form.errors.auto_approve" class="text-red-600">{{ form.errors.auto_approve }}</small>
                    </div>

                    <!-- Submit Button -->
                    <div class="col-span-12 flex justify-end gap-2 pt-2">
                        <Button
                            :label="$t('Cancel')"
                            severity="secondary"
                            @click="() => {
                                try {
                                    const routeName = proxy.route ? proxy.route('sites.index') : '/admin/sites';
                                    showLoading('Loading sites...');
                                    proxy.$inertia.visit(routeName, { onFinish: () => hideLoading() });
                                } catch (e) {
                                    hideLoading();
                                    window.location.href = '/admin/sites';
                                }
                            }"
                        />
                        <Button
                            :label="$t('Save')"
                            type="submit"
                            :loading="form.processing"
                            :disabled="form.processing || urlValidation.status === 'error'"
                            :severity="urlValidation.status === 'warning' ? 'warning' : 'primary'"
                        />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>

<style>
.required-field {
    color: red;
}
</style>
